import { useCallback, useMemo, useState } from 'react';
import type { DigitalCardFace } from 'src/components/_common/DigitalCard/context';

/**
 * [디지털 카드의 모든 리소스(이미지, 비디오 등) 로딩 상태를 관리하는 훅]
 *
 * @description
 * 카드의 모든 리소스(이미지, 비디오) 로딩 상태를 추적하고 관리합니다.
 * CSS 로딩 상태와 통합하여, CSS와 리소스가 모두 로드될 때까지 로딩 상태를 유지합니다.
 *
 * 리소스 추적은 src URL 기반으로 수행됩니다. faceIdx와 무관하게 같은 src는 한 번만 추적되며,
 * 이를 통해 flip 시 face가 복제되어도 리소스 로딩 상태가 올바르게 유지됩니다.
 *
 * @param visibleFaces - 현재 표시되는 카드 면들
 * @param isStylesLoaded - CSS 로딩 완료 여부 (선택적)
 *
 * @return 모든 리소스 로딩 상태, 로딩 실패 상태, 로딩 완료된 리소스 목록, 로딩 완료 핸들러, 로딩 실패 핸들러
 * ```ts
 * {
 *   isAllResourcesLoaded: boolean // 모든 리소스(CSS + 이미지 + 비디오) 로딩 상태
 *   isAllResourceLoadError: boolean // 리소스 로딩 실패 상태
 *   completedResources: Set<string> // 로딩 완료된 리소스 URL Set
 *   handleResourceLoadComplete: (src: string) => void // 리소스 로딩 완료 핸들러
 *   handleResourceLoadError: () => void // 리소스 로딩 실패 핸들러
 * }
 * ```
 */
export const useAllResourceLoad = (visibleFaces: Array<{ face: DigitalCardFace }>, isStylesLoaded?: boolean) => {
  const [isAllResourceLoadError, setIsAllResourceLoadError] = useState(false);
  const [completedResources, setCompletedResources] = useState<Set<string>>(new Set());

  // 모든 리소스 URL 추출 (src만 Set으로 관리)
  const allResourceUrls = useMemo(() => {
    const urls = new Set<string>();
    visibleFaces.forEach(({ face }) => {
      face.layers.forEach((layer) => {
        if (layer.type === 'image') urls.add(layer.imgSrc);
        if (layer.type === 'video') urls.add(layer.videoSrc);
        if (layer.type === 'shader') {
          urls.add(`shader:${layer.idx}`);
        }
      });
    });
    return urls;
  }, [visibleFaces]);

  // 리소스 로딩 완료 핸들러 (src 기반으로 중복 체크)
  const handleResourceLoadComplete = useCallback((src: string) => {
    setCompletedResources((prev) => {
      if (prev.has(src)) return prev;
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  }, []);

  const handleResourceLoadError = useCallback(() => {
    setIsAllResourceLoadError(true);
  }, []);

  // 모든 리소스(CSS + 이미지 + 비디오) 로딩 상태
  const isAllResourcesLoaded = useMemo(() => {
    const areAllResourcesLoaded =
      completedResources.size >= allResourceUrls.size && [...allResourceUrls].every((url) => completedResources.has(url));
    const areStylesLoaded = isStylesLoaded ?? true; // CSS 로딩 상태가 없으면 true로 간주
    return areAllResourcesLoaded && areStylesLoaded;
  }, [completedResources, allResourceUrls, isStylesLoaded]);

  return {
    isAllResourcesLoaded,
    isAllResourceLoadError,
    completedResources,
    handleResourceLoadComplete,
    handleResourceLoadError,
  };
};
